import { useDeepgramSocket } from '~/composables/useDeepgramSocket';

export const useSpeechRecognition = () => {
    const socket = useDeepgramSocket();

    const isSupported = ref(typeof MediaRecorder !== 'undefined');
    const isRecording = ref(false);
    const isTranscribing = ref(false);
    const transcript = ref('');

    let mediaRecorder: MediaRecorder | null = null;
    let finalTranscript = '';
    let interimTranscript = '';

    // --- Deepgram streaming path ---

    const setupTranscriptHandler = () => {
        const ws = socket.getWs();
        if (!ws) return;

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            const alt = data.channel?.alternatives?.[0];
            if (!alt) return;

            if (data.is_final) {
                if (alt.transcript) {
                    finalTranscript += (finalTranscript ? ' ' : '') + alt.transcript;
                }
                interimTranscript = '';
            } else {
                interimTranscript = alt.transcript || '';
            }

            transcript.value = (finalTranscript + ' ' + interimTranscript).trim();
        };
    };

    const startStreaming = async () => {
        const connected = await socket.ensureConnected();
        if (!connected) return;

        socket.stopKeepAlive();
        socket.clearIdleTimeout();

        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            return;
        }

        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';

        mediaRecorder = new MediaRecorder(stream, { mimeType });

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0 && socket.isWsOpen()) {
                socket.getWs()!.send(e.data);
            }
        };

        setupTranscriptHandler();

        const ws = socket.getWs()!;
        ws.onerror = () => {};
        ws.onclose = () => {};

        mediaRecorder.start(200);
        isRecording.value = true;
    };

    const stopStreaming = async (): Promise<string> => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
            mediaRecorder.stream.getTracks().forEach((t) => t.stop());
            mediaRecorder = null;
        }
        isRecording.value = false;

        // Disconnect and immediately preconnect a fresh socket
        // so the next recording starts instantly.
        socket.reconnect();

        const rawText = (finalTranscript + ' ' + interimTranscript).trim();
        if (rawText) {
            transcript.value = rawText;
        }

        finalTranscript = '';
        interimTranscript = '';

        return transcript.value;
    };

    // --- Public API ---

    const preconnect = () => socket.preconnect(() => socket.disconnect());
    const disconnect = () => socket.disconnect();

    const start = async () => {
        if (!isSupported.value || isRecording.value) return;

        finalTranscript = '';
        interimTranscript = '';
        transcript.value = '';

        await startStreaming();
    };

    const stop = (): Promise<string> => {
        return stopStreaming();
    };

    const cancel = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
            mediaRecorder.stream.getTracks().forEach((t) => t.stop());
            mediaRecorder = null;
        }

        socket.disconnect();
        isRecording.value = false;
        isTranscribing.value = false;
        finalTranscript = '';
        interimTranscript = '';
    };

    onScopeDispose(() => cancel());

    return {
        isSupported,
        isRecording,
        isTranscribing,
        transcript,
        preconnect,
        disconnect,
        start,
        stop,
        cancel,
    };
};
